// galaxy/shade.js: the 3D disk model in GLSL, shared by the glow raymarch and the stars.
// Face-on maps (map.js) give surface brightness and the dust column; exponential
// vertical profiles turn them into 3D densities. Scale heights: old thin disk
// 300 pc (Juric+2008), young OB layer ~70 pc, dust ~100 pc (Drimmel & Spergel 2001:
// 134 pc; Marshall+2006: 125 pc; thinner here for crisper lanes). Units: kpc.
import { MAP_R, BAR_PHI } from './model.js';
import { ENC } from './map.js';

const f = (x) => (Number.isInteger(x) ? x.toFixed(1) : String(x));

export const GLSL_DISK = /* glsl */`
uniform sampler2D uMapA, uMapB;
const float MAPR = ${f(MAP_R)};
const float BARPHI = ${BAR_PHI.toFixed(6)};
const float H_OLD = 0.30, H_YNG = 0.07, H_DUST = 0.10;
const float TEXEL = ${f(2 * MAP_R)} / 1024.0;
// dust scale height: thinner toward the centre (artistic: crisp lanes in silhouette against the bulge)
float hDust(vec2 xz) { return mix(0.035, H_DUST, smoothstep(0.8, 4.0, length(xz))); }
vec2 mapUV(vec2 xz) { return xz * (0.5 / MAPR) + 0.5; }
// rgb = old-star surface brightness, a = vertical dust optical depth
vec4 mapA(vec2 xz, float lod) { vec4 t = textureLod(uMapA, mapUV(xz), lod); return vec4(t.rgb * t.rgb * ${f(ENC.col)}, t.a * t.a * ${f(ENC.dust)}); }
// rgb = young-star + HII surface brightness, a = young-star density (0..1)
vec4 mapB(vec2 xz, float lod) { vec4 t = textureLod(uMapB, mapUV(xz), lod); return vec4(t.rgb * t.rgb * ${f(ENC.col)}, t.a * t.a); }
// normalised vertical profile exp(-|y|/h)/(2h) and its antiderivative
float vprof(float y, float h) { return exp(-abs(y) / h) / (2.0 * h); }
float vcdf(float y, float h) { return sign(y) * 0.5 * (1.0 - exp(-abs(y) / h)); }
// world -> bar frame: u along the near end, y up, v across
vec3 toBar(vec3 p) { float c = cos(BARPHI), s = sin(BARPHI); return vec3(p.x * c + p.z * s, p.y, -p.x * s + p.z * c); }
// bulge + bar + nuclear disk luminosity density (map units / kpc^3). Artistic
// fit to the COBE/VVV picture: a boxy bulge (~1.1 x 0.45 x 0.38 kpc 1-sigma)
// inside a thin long bar reaching ~4.6 kpc (Wegg, Gerhard & Portail 2015).
float bulgeRho(vec3 p, out float nuc) {
  vec3 b = toBar(p);
  // boxy exponential bulge (Dwek+1995 E-type shape, boxy c = 2.4) + a wider halo of bulge light
  vec3 q = b / vec3(0.8, 0.3, 0.38);
  float rs = pow(pow(abs(q.x), 2.4) + pow(abs(q.y), 2.4) + pow(abs(q.z), 2.4), 1.0 / 2.4);
  float boxy = 2.2 * exp(-rs) + 0.25 * exp(-0.5 * dot(b / vec3(1.6, 0.6, 0.8), b / vec3(1.6, 0.6, 0.8)));
  float lb = exp(-0.5 * (b.z * b.z) / (0.75 * 0.75) - abs(b.y) / 0.2) * (1.0 - smoothstep(3.6, 4.8, abs(b.x))) * (0.35 + 0.65 * exp(-abs(b.x) / 2.2));
  // nuclear stellar disk (~150 pc) + nuclear star cluster (~5-10 pc, around Sgr A*)
  float r = length(b / vec3(1.0, 0.35, 1.0));
  nuc = 5.0 * exp(-r / 0.12) + 40.0 * exp(-length(b) / 0.02);
  return boxy + 0.8 * lb + nuc;
}
// dust optical depth from a to b (K sub-segments, clipped to the dust slab;
// the vertical profile is integrated exactly inside each sub-segment)
float dustTau(vec3 a, vec3 b, float lod, int K) {
  const float HC = 0.45;
  vec3 d = b - a;
  float t0 = 0.0, t1 = 1.0;
  if (abs(d.y) > 1e-7) {
    float ta = (HC - a.y) / d.y, tb = (-HC - a.y) / d.y;
    t0 = max(t0, min(ta, tb)); t1 = min(t1, max(ta, tb));
  } else if (abs(a.y) > HC) return 0.0;
  if (t1 <= t0) return 0.0;
  float len = length(d) * (t1 - t0) / float(K);
  float tau = 0.0;
  for (int k = 0; k < 4; k++) {
    if (k >= K) break;
    float sa = mix(t0, t1, float(k) / float(K)), sb = mix(t0, t1, float(k + 1) / float(K));
    vec3 pm = a + d * (0.5 * (sa + sb));
    float ya = a.y + d.y * sa, yb = a.y + d.y * sb;
    float hd = hDust(pm.xz);
    float v = abs(yb - ya) > 1e-5 ? (vcdf(yb, hd) - vcdf(ya, hd)) / (yb - ya) : vprof(ya, hd);
    tau += mapA(pm.xz, lod).a * v * len;
  }
  return tau;
}
`;
